/**
 * HUB-107 / COP-152 — MCP server registry (admin). One card per server plus an
 * "Add server" card. Each card holds the connection settings for ONE auth type
 * from the closed menu, write-only secrets (presence flags only), and the tool
 * allowlist: Discover lists the live tools, the admin approves each one with a
 * kind (default WRITE — fail closed), and Save FREEZES the approved tool's
 * description and schema. Who may use the server is a Permissions grant on
 * `mcp_server:<id>` (read / write).
 *
 * The card is pure/controlled so the no-jsdom test renders it statically.
 */
import { useEffect, useState } from 'react';
import {
  MCP_AUTH_TYPES,
  type McpApprovedTool,
  type McpAuthType,
  type McpServer,
  type McpServerWriteBody,
} from '@verybigthings/trackframe-shared/schemas';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { SecretField } from '@/components/SecretField';
import { hub } from '@/lib/hub-client';

export const AUTH_TYPE_COPY: Record<McpAuthType, { label: string; hint: string }> = {
  oauth: { label: 'OAuth (each user connects)', hint: 'Each user clicks Connect and signs in to the service.' },
  user_key: { label: 'Personal key (each user pastes)', hint: 'Each user pastes their own API key or token.' },
  hub_identity: {
    label: 'Hub identity (automatic)',
    hint: 'The hub sends a short-lived signed token naming the user. For your own MCP servers: verify it against the hub JWKS.',
  },
  shared_key: { label: 'Shared key (read tools only)', hint: 'One key for everyone. It is not a user, so only read tools may be approved.' },
};

export interface ServerForm {
  slug: string;
  displayName: string;
  url: string;
  authType: McpAuthType;
  oauthClientId: string;
  oauthClientSecret: string;
  oauthScopes: string;
  keyHeader: string;
  sharedKey: string;
  enabled: boolean;
}

export const EMPTY_SERVER_FORM: ServerForm = {
  slug: '', displayName: '', url: '', authType: 'oauth', oauthClientId: '', oauthClientSecret: '',
  oauthScopes: '', keyHeader: '', sharedKey: '', enabled: false,
};

export function formFromServer(server: McpServer): ServerForm {
  return {
    slug: server.slug,
    displayName: server.displayName,
    url: server.url,
    authType: server.authType,
    oauthClientId: server.oauthClientId ?? '',
    oauthClientSecret: '',
    oauthScopes: server.oauthScopes ?? '',
    keyHeader: server.keyHeader ?? '',
    sharedKey: '',
    enabled: server.enabled,
  };
}

/** A live tool as Discover returns it. */
export interface DiscoveredTool {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  readOnlyHint?: boolean;
  toolName: string | null;
  approved: boolean;
  changed: boolean;
}

/** The admin's choice for one discovered tool. */
export interface ToolChoice {
  approved: boolean;
  kind: 'read' | 'write';
}

/**
 * PUT/POST body. Write-only secrets are sent ONLY when typed (blank keeps the
 * stored one). `tools` is sent only after a Discover, so a settings-only save
 * never touches the allowlist.
 */
export function serverWriteBody(form: ServerForm, tools?: McpApprovedTool[]): McpServerWriteBody {
  const optional = (value: string) => (value.trim() === '' ? null : value.trim());
  return {
    slug: form.slug.trim(),
    displayName: form.displayName.trim(),
    url: form.url.trim(),
    authType: form.authType,
    enabled: form.enabled,
    oauthClientId: optional(form.oauthClientId),
    oauthScopes: optional(form.oauthScopes),
    keyHeader: optional(form.keyHeader),
    ...(form.oauthClientSecret ? { oauthClientSecret: form.oauthClientSecret } : {}),
    ...(form.sharedKey ? { sharedKey: form.sharedKey } : {}),
    ...(tools !== undefined ? { tools } : {}),
  };
}

/** Freezes the approved live tools (description + schema as seen NOW). */
export function approvedToolsFrom(
  live: DiscoveredTool[],
  choices: Record<string, ToolChoice>,
  existing: McpApprovedTool[],
): McpApprovedTool[] {
  const previous = new Map(existing.map((t) => [t.name, t]));
  return live
    .filter((t) => choices[t.name]?.approved)
    .map((t) => {
      const confirmationMessage = previous.get(t.name)?.confirmationMessage;
      return {
        name: t.name,
        description: t.description,
        inputSchema: t.inputSchema,
        kind: choices[t.name]!.kind,
        ...(confirmationMessage ? { confirmationMessage } : {}),
      };
    });
}

/** Initial choices: approved tools keep their kind; new tools default to WRITE (fail closed). */
export function initialChoices(live: DiscoveredTool[], existing: McpApprovedTool[]): Record<string, ToolChoice> {
  const previous = new Map(existing.map((t) => [t.name, t]));
  return Object.fromEntries(live.map((t) => [
    t.name,
    { approved: t.approved, kind: previous.get(t.name)?.kind ?? 'write' },
  ]));
}

export interface ServerCardProps {
  server: McpServer | null;
  form: ServerForm;
  discovered: DiscoveredTool[] | null;
  choices: Record<string, ToolChoice>;
  busy: boolean;
  error: string | null;
  onForm: (patch: Partial<ServerForm>) => void;
  onChoice: (name: string, choice: ToolChoice) => void;
  onDiscover: () => void;
  onSave: () => void;
  onDelete: () => void;
}

function Field(props: { id: string; label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={props.id}>{props.label}</Label>
      <Input id={props.id} value={props.value} placeholder={props.placeholder} autoComplete="off"
        onChange={(e) => props.onChange(e.target.value)} />
    </div>
  );
}

function ToolsTable(props: Pick<ServerCardProps, 'discovered' | 'choices' | 'onChoice' | 'form'>) {
  const { discovered, choices, onChoice, form } = props;
  if (!discovered) return null;
  const writeAllowed = form.authType !== 'shared_key';
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-muted-foreground">
          <th className="py-1">Approve</th><th>Tool</th><th>Kind</th><th>Status</th>
        </tr>
      </thead>
      <tbody>
        {discovered.map((tool) => {
          const choice = choices[tool.name] ?? { approved: false, kind: 'write' };
          const usable = tool.toolName !== null;
          return (
            <tr key={tool.name} className="border-t align-top">
              <td className="py-2">
                <input type="checkbox" aria-label={`Approve ${tool.name}`} disabled={!usable}
                  checked={choice.approved} onChange={(e) => onChoice(tool.name, { ...choice, approved: e.target.checked })} />
              </td>
              <td className="py-2">
                <div className="font-mono">{tool.name}</div>
                <div className="text-xs text-muted-foreground">{tool.description}</div>
                {tool.readOnlyHint && <div className="text-xs text-muted-foreground">Server hint: read-only (not trusted)</div>}
              </td>
              <td className="py-2">
                <Select aria-label={`Kind for ${tool.name}`} value={choice.kind}
                  onChange={(e) => onChoice(tool.name, { ...choice, kind: e.target.value as 'read' | 'write' })}>
                  <option value="read">read</option>
                  {writeAllowed && <option value="write">write (asks the user first)</option>}
                </Select>
              </td>
              <td className="py-2 text-xs">
                {!usable ? 'Name too long' : tool.changed ? 'Changed on server — re-approve' : tool.approved ? 'Approved' : 'New'}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function ServerCard(props: ServerCardProps) {
  const { server, form, busy, error, onForm, onDiscover, onSave, onDelete } = props;
  const id = server?.id ?? 'new';
  return (
    <Card data-mcp-server={server?.slug ?? 'new'}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{server ? server.displayName : 'Add an MCP server'}</CardTitle>
          {server && (
            <span className={server.enabled ? 'text-xs font-medium text-emerald-600' : 'text-xs text-muted-foreground'}>
              {server.enabled ? 'Enabled' : 'Disabled'} · {server.tools.length} tools · {server.connectionCount} connections
            </span>
          )}
        </div>
        <CardDescription>
          {server
            ? <>Grant users <code>mcp_server:{server.id}</code> read or write on the Permissions page.</>
            : 'A remote MCP server (Streamable HTTP). Tools reach the copilot only after you approve them.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <Field id={`${id}-slug`} label="Slug (tool prefix)" value={form.slug} placeholder="slack" onChange={(slug) => onForm({ slug })} />
          <Field id={`${id}-name`} label="Display name" value={form.displayName} onChange={(displayName) => onForm({ displayName })} />
        </div>
        <Field id={`${id}-url`} label="Server URL" value={form.url} placeholder="https://example.com/mcp" onChange={(url) => onForm({ url })} />
        <div className="space-y-2">
          <Label htmlFor={`${id}-auth`}>How users connect</Label>
          <Select id={`${id}-auth`} className="w-full" value={form.authType}
            onChange={(e) => onForm({ authType: e.target.value as McpAuthType })}>
            {MCP_AUTH_TYPES.map((type) => <option key={type} value={type}>{AUTH_TYPE_COPY[type].label}</option>)}
          </Select>
          <p className="text-xs text-muted-foreground">{AUTH_TYPE_COPY[form.authType].hint}</p>
        </div>
        {form.authType === 'oauth' && (
          <>
            <Field id={`${id}-client`} label="OAuth client id (blank = register automatically)" value={form.oauthClientId}
              onChange={(oauthClientId) => onForm({ oauthClientId })} />
            <SecretField id={`${id}-secret`} label="OAuth client secret" hasSecret={server?.hasOauthClientSecret ?? false}
              value={form.oauthClientSecret} onChange={(oauthClientSecret) => onForm({ oauthClientSecret })} />
            <Field id={`${id}-scopes`} label="Scopes (blank = server default)" value={form.oauthScopes}
              onChange={(oauthScopes) => onForm({ oauthScopes })} />
            {server && <p className="text-xs text-muted-foreground">Redirect URI: <code>{server.callbackUrl}</code></p>}
          </>
        )}
        {(form.authType === 'user_key' || form.authType === 'shared_key') && (
          <Field id={`${id}-header`} label="Key header (blank = Authorization: Bearer)" value={form.keyHeader}
            onChange={(keyHeader) => onForm({ keyHeader })} />
        )}
        {form.authType === 'shared_key' && (
          <SecretField id={`${id}-shared`} label="Shared key" hasSecret={server?.hasSharedKey ?? false}
            value={form.sharedKey} onChange={(sharedKey) => onForm({ sharedKey })} />
        )}
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.enabled} onChange={(e) => onForm({ enabled: e.target.checked })} />
          Enabled
        </label>
        <ToolsTable {...props} />
        {error && <Alert variant="destructive">{error}</Alert>}
        <div className="flex items-center gap-2">
          <Button type="button" onClick={onSave} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>
          {server && <Button type="button" variant="outline" onClick={onDiscover} disabled={busy}>Discover tools</Button>}
          {server && <Button type="button" variant="outline" onClick={onDelete} disabled={busy}>Delete</Button>}
        </div>
      </CardContent>
    </Card>
  );
}

// --- container ---

interface CardState {
  form: ServerForm;
  discovered: DiscoveredTool[] | null;
  choices: Record<string, ToolChoice>;
  error: string | null;
}

const NEW_KEY = 'new';

export function McpServers() {
  const [servers, setServers] = useState<McpServer[]>([]);
  const [cards, setCards] = useState<Record<string, CardState>>({});
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = async () => {
    try {
      const { servers: rows } = await hub.mcpServersList();
      setServers(rows);
      setCards(Object.fromEntries([
        [NEW_KEY, { form: { ...EMPTY_SERVER_FORM }, discovered: null, choices: {}, error: null }],
        ...rows.map((s) => [s.id, { form: formFromServer(s), discovered: null, choices: {}, error: null }]),
      ]));
    } catch (err) {
      setLoadError((err as Error).message);
    }
  };
  useEffect(() => { void load(); }, []);

  const patch = (key: string, next: Partial<CardState>) =>
    setCards((prev) => ({ ...prev, [key]: { ...prev[key]!, ...next } }));

  const run = async (key: string, action: () => Promise<void>) => {
    setBusyKey(key);
    patch(key, { error: null });
    try {
      await action();
    } catch (err) {
      patch(key, { error: (err as Error).message });
    } finally {
      setBusyKey(null);
    }
  };

  const save = (server: McpServer | null) => run(server?.id ?? NEW_KEY, async () => {
    const card = cards[server?.id ?? NEW_KEY]!;
    const tools = server && card.discovered ? approvedToolsFrom(card.discovered, card.choices, server.tools) : undefined;
    const body = serverWriteBody(card.form, tools);
    if (server) await hub.mcpServerUpdate({ params: { id: server.id }, body });
    else await hub.mcpServerCreate({ body });
    await load();
  });

  const discover = (server: McpServer) => run(server.id, async () => {
    const { tools } = await hub.mcpServerDiscover({ params: { id: server.id } });
    patch(server.id, { discovered: tools, choices: initialChoices(tools, server.tools) });
  });

  const remove = (server: McpServer) => run(server.id, async () => {
    await hub.mcpServerDelete({ params: { id: server.id } });
    await load();
  });

  const renderCard = (server: McpServer | null) => {
    const key = server?.id ?? NEW_KEY;
    const card = cards[key];
    if (!card) return null;
    return (
      <ServerCard
        key={key}
        server={server}
        form={card.form}
        discovered={card.discovered}
        choices={card.choices}
        busy={busyKey === key}
        error={card.error}
        onForm={(p) => patch(key, { form: { ...card.form, ...p } })}
        onChoice={(name, choice) => patch(key, { choices: { ...card.choices, [name]: choice } })}
        onDiscover={() => server && void discover(server)}
        onSave={() => void save(server)}
        onDelete={() => server && void remove(server)}
      />
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">MCP Servers</h1>
        <p className="text-sm text-muted-foreground">
          Remote tool servers for the copilot. Every call uses the signed-in user&apos;s own identity; writes ask first.
        </p>
      </div>
      {loadError && <Alert variant="destructive">{loadError}</Alert>}
      {servers.map((server) => renderCard(server))}
      {renderCard(null)}
    </div>
  );
}
